import './UsersTable.scss';
import { AsyncStateNotification } from '@ui/notifications';
import { InviteModal } from './InviteModal';
import { securityActions } from '@sonar-software/sonar-security';
import { SonarTable } from '@ui/SonarTable';
import { Radio } from 'antd';
import { COPY_SPECIFIC, NOTIFICATIONS_COPY } from '@sonar-software/sonar-ts-constants';
import { coreActions, FeatureFlag } from '@sonar-software/sonar-core';
import { createAsyncSelectors, createSonarSelector } from '@lib/selectors';
import { getUserNameColumn, getUserOwnerCountColumn, getUserReadOnlyColumn, getUserStakeholderCountColumn, getUserStatusColumn, userEmailColumn } from './UsersTableColumns';
import React, { useCallback, useMemo, useState } from 'react';
import { useAction, useCurrentUser, useFeatureFlag, useSonarSelector, useUsers } from '@hooks';
const inviteLinkSelectors = createAsyncSelectors((state) => state.security.invitations.link.get);
const linkSelector = createSonarSelector((state) => { var _a; return (_a = inviteLinkSelectors.data(state)) === null || _a === void 0 ? void 0 : _a.link; });
const getRowKey = (user) => user.id;
const viewOptions = [
    {
        label: COPY_SPECIFIC.ACTIVE,
        value: 'active'
    }, {
        label: COPY_SPECIFIC.INVITED,
        value: 'invited'
    }, {
        label: COPY_SPECIFIC.LOCKED,
        value: 'locked'
    }
];
export const UsersTable = () => {
    const [viewType, setViewType] = useState('active');
    const [inviteUser, setInviteUser] = useState(null);
    const [showInviteModal, setShowInviteModal] = useState(false);
    const { isReadOnly, id: userId } = useCurrentUser();
    const hasDataDictionary = useFeatureFlag(FeatureFlag.DataDictionary);
    const users = useUsers(true);
    const isLoading = useSonarSelector(useUsers.selectors.isRequested);
    const userInviteLinkRequesting = useSonarSelector(inviteLinkSelectors.isRequested);
    const userInviteLink = useSonarSelector(linkSelector);
    const requestPostUser = useAction(coreActions.requestPostCoreUsersById);
    const requestInviteLink = useAction(securityActions.requestGetSecurityInvitationsLink);
    const resetRequestInviteLink = useAction(securityActions.resetGetSecurityInvitationsLink);
    const onAfterCloseInviteModal = useCallback(() => {
        resetRequestInviteLink();
        setInviteUser(null);
    }, [resetRequestInviteLink]);
    const handleOnCancelInviteModal = useCallback(() => {
        setShowInviteModal(false);
    }, []);
    const readOnlyClickHandler = useCallback((user) => {
        requestPostUser({
            isReadOnly: !user.isReadOnly,
            userId: user.id
        });
    }, [requestPostUser]);
    const inviteClickHandler = useCallback((user) => {
        requestInviteLink({
            email: user.email
        });
        setInviteUser(user);
        setShowInviteModal(true);
    }, [requestInviteLink]);
    const lockToggleHandler = useCallback((id, isLocked) => {
        requestPostUser({
            isLocked,
            userId: id
        });
    }, [requestPostUser]);
    const columns = useMemo(() => [
        getUserNameColumn(userId),
        userEmailColumn,
        getUserReadOnlyColumn(userId, isReadOnly, readOnlyClickHandler),
        getUserOwnerCountColumn(!hasDataDictionary),
        getUserStakeholderCountColumn(!hasDataDictionary),
        getUserStatusColumn(userId, isReadOnly || userInviteLinkRequesting, inviteClickHandler, lockToggleHandler)
    ], [
        hasDataDictionary,
        inviteClickHandler,
        isReadOnly,
        lockToggleHandler,
        readOnlyClickHandler,
        userId,
        userInviteLinkRequesting
    ]);
    function filterUsers(user) {
        switch (viewType) {
            case 'active':
                return !user.isLocked && !user.isInvited;
            case 'invited':
                return !user.isLocked && user.isInvited;
            case 'locked':
                return user.isLocked;
            default:
                return true;
        }
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(Radio.Group, { buttonStyle: 'solid', className: 'user-view-options', onChange: (e) => setViewType(e.target.value), options: viewOptions, optionType: 'button', value: viewType }),
        React.createElement(SonarTable, { bordered: false, columns: columns, dataSource: users.filter(filterUsers), id: 'users-table', loading: isLoading, pagination: false, rowKey: getRowKey, scroll: {
                x: 'max-content',
                y: 'max-content'
            }, sticky: true }),
        userInviteLink && inviteUser &&
            React.createElement(InviteModal, { afterClose: onAfterCloseInviteModal, inviteLink: userInviteLink, onCancel: handleOnCancelInviteModal, open: showInviteModal, user: inviteUser }),
        React.createElement(AsyncStateNotification, { errored: NOTIFICATIONS_COPY.ERROR_GET_USER_INVITE_LINK, failed: NOTIFICATIONS_COPY.FAILED_GET_USER_INVITE_LINK, stateSelector: inviteLinkSelectors.state })));
};
